import axios from 'axios';
import { getCurrentApiHost, API_NAMESPACE, API_VERSION } from 'config/environment';
// Single-flight guard: triggerOtp is invoked independently from more than one
// caller (link-action.tsx, hooks/use-login-with-token.ts) with no shared
// knowledge of whether a request for the same token is already in flight. A
// duplicate call for a token that's already being requested (extra render,
// racing mounts) reuses the pending request instead of firing a second POST —
// the backend mints a brand-new OTP per call and overwrites the unconsumed
// one, so an extra request silently invalidates the code already emailed.
// Cleared once the request settles so a later legitimate resend still fires.
const inFlightTriggerOtpRequests = new Map();
// Survives remount / second page load of the same magic-link URL (email
// scanner prefetch + user click). In-flight Map alone cannot — fresh JS heap
// after the first POST settles. Resend passes { force: true }.
const OTP_TRIGGER_COOLDOWN_MS = 60000;
const otpTriggeredStorageKey = (token)=>`rl:otp-triggered:${token}`;
const wasRecentlyTriggered = (token)=>{
    try {
        const prev = sessionStorage.getItem(otpTriggeredStorageKey(token));
        if (!prev) {
            return false;
        }
        const age = Date.now() - Number(prev);
        return Number.isFinite(age) && age >= 0 && age < OTP_TRIGGER_COOLDOWN_MS;
    } catch (unused) {
        return false;
    }
};
const markTriggered = (token)=>{
    try {
        sessionStorage.setItem(otpTriggeredStorageKey(token), String(Date.now()));
    } catch (unused) {
    // Private mode / quota — fall back to in-flight Map only.
    }
};
export const triggerOtp = (token, options = {})=>{
    const { force = false } = options;
    const inFlightRequest = inFlightTriggerOtpRequests.get(token);
    if (inFlightRequest) {
        return inFlightRequest;
    }
    if (!force && wasRecentlyTriggered(token)) {
        return Promise.resolve({
            data: {},
            skipped: true
        });
    }
    const url = `${getCurrentApiHost()}/${API_NAMESPACE}/${API_VERSION}/users/trigger-otp?token=${token}`;
    // Latch before the network round-trip so a remount after settle (or after a
    // failed response) cannot mint a second code for the same token open.
    markTriggered(token);
    const request = axios.post(url).finally(()=>{
        inFlightTriggerOtpRequests.delete(token);
    });
    inFlightTriggerOtpRequests.set(token, request);
    return request;
};
export const validateOtp = async (token, otpInput)=>{
    const url = `${getCurrentApiHost()}/${API_NAMESPACE}/${API_VERSION}/users/validate-otp?token=${token}`;
    const response = await axios.post(url, {
        otp: otpInput
    });
    return response.data;
};
